import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIonRouter } from '@ionic/react';
import { TabsToolbarFeature } from '@mindly/ui-components';
import { userSelector } from 'src/redux/user/selector';
import { useSelector } from 'react-redux';
import { tabBarSelector } from 'src/redux/tabBar/selector';
import { PrivateRoutes } from 'src/routes/navigation';
import classNames from 'classnames';
import { ANIMATION_DURATION } from 'src/shared/utils/pageAnimation';
import { selectTotalUnReadMessages } from '../../redux/chatMessages/selector';
import { useTranslation } from 'react-i18next';
import { isStreamChatEnabledSelector, unreadMessageCountSelector } from 'src/redux/chat/selector';
import { useChat } from 'src/shared/hooks/useChat';

const TabsBarNew: React.FC = ({ children }) => {
  const user = useSelector(userSelector);
  const tabBar = useSelector(tabBarSelector);
  const navigation = useIonRouter();
  const { t } = useTranslation('ui');
  const unReadMessages = useSelector(selectTotalUnReadMessages(user?.id ?? ''));

  const [hidden, setHidden] = useState<boolean>(true);
  const isMounted = useRef<boolean>(false);
  const isStreamChatEnabled = useSelector(isStreamChatEnabledSelector);
  const newUnreadMessage = useSelector(unreadMessageCountSelector);
  const { handleChatClick } = useChat();

  const unreadMessagesCount = useMemo(() => {
    if (isStreamChatEnabled === false) return unReadMessages.length;

    return newUnreadMessage;
  }, [unReadMessages, newUnreadMessage, isStreamChatEnabled]);

  useEffect(() => {
    if (user?.id && tabBar.isActive) {
      setHidden(false);
    } else {
      if (isMounted.current) {
        setTimeout(() => {
          setHidden(true);
        }, ANIMATION_DURATION - 50);
      } else {
        setHidden(true);
      }
    }
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, [user?.id, tabBar.isActive]);

  const onTabWillChange = useCallback(
    (e: CustomEvent<{ tab: string }>) => {
      switch (e.detail.tab) {
        case PrivateRoutes.CHATS:
          handleChatClick();
          break;
        default:
          // to prevent animation between tabs
          navigation.push(e.detail.tab, 'none');
      }
    },
    [handleChatClick]
  );

  return (
    <TabsToolbarFeature
      user={user ?? undefined}
      slot={'bottom'}
      selectedTab={navigation.routeInfo.pathname}
      className={classNames('tabs-bar-feature', {
        '!hidden': hidden,
      })}
      onTabWillChange={onTabWillChange}
      items={[
        {
          label: t('tabsBar.schedule'),
          icon: 'schedule',
          href: PrivateRoutes.CONSULTATION,
        },
        {
          label: t('tabsBar.list'),
          icon: 'list',
          href: PrivateRoutes.PSYCHOLOGIST_LIST,
        },
        {
          label: t('tabsBar.chat'),
          icon: 'chat',
          href: PrivateRoutes.CHATS,
          badge: unreadMessagesCount,
        },
        {
          label: t('tabsBar.profile'),
          icon: 'profile',
          href: PrivateRoutes.PROFILE_SETTINGS_TAB,
        },
      ]}
    >
      {children}
    </TabsToolbarFeature>
  );
};

export default TabsBarNew;
